import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Button,
  Stack,
  IconButton,
  CircularProgress,
} from "@mui/material";

import { FiX } from "react-icons/fi";

import { apiRequest } from "../../api/apiHelper";

export default function StageTransitionForm({
  leadData = {},
  onSubmit,
  onCancel,
}) {
  const [loading, setLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [form, setForm] = useState({
    id: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailId: "",
    city: "",
    name: "",
    age: "",
    brandName: "",
    commercialVehicleAge: "",
    previousVehicleOwnership: "",
    previousVehicleFuelType: "",
    previousVehicleSegment: "",
    whoWillDrive: "",
    ownership: "",
    callStatus: "",
    connectedOutcome: "",
    notConnectedOutcome: "",
    leadSource: "",
    qualification: "",
    fromWhereGetNumber: "",
    description: "",

  });

  // Demo scheduled details
  const [demoDetails, setDemoDetails] = useState({
    customerName: "",
    customerMobile: "",
    classification: "",
    findCity: "",
    hotCold: "",
    address1: "",
    address2: "",
    googleMaps: "",
  });

  // Fetch existing data when component mounts
  useEffect(() => {
    const fetchExistingData = async () => {
      // Defensive: Only access _id and id if leadData is not null
      const dealId = leadData && leadData._id ? leadData._id : undefined;
      const leadId = leadData && leadData.id ? leadData.id : undefined;

      if (!dealId && !leadId) {
        // No existing data, just use leadData as is
        setForm(prev => ({ ...prev, ...leadData }));
        setDataFetched(true);
        return;
      }

      setLoading(true);
      try {
        let response;
        if (dealId) {
          // Fetch deal data
          response = await apiRequest("get", `/stage-detail/stage-transition-form-get?dealId=${dealId}`);
        } else if (leadId) {
          // Fetch lead data
          response = await apiRequest("get", `/stage-detail/stage-transition-form-get?id=${leadId}`);
        }

        if (response && response.success) {
          const data = response.deal || response.lead;
          const extraData = data.extraData || {};

          // Only set hasExistingData if extraData has stage transition form data
          const hasStageTransitionData = extraData && (
            extraData.callStatus || 
            extraData.connectedOutcome || 
            extraData.leadSource ||
            extraData.qualification

          );
          
          setHasExistingData(hasStageTransitionData);

          // Merge fetched data into form
          setForm(prev => ({
            ...prev,
            id: data.id || data._id || "",
            firstName: extraData.firstName || "",
            lastName: extraData.lastName || "",
            phoneNumber: extraData.phoneNumber || data.phone || "",
            emailId: extraData.emailId || data.email || "",
            city: extraData.city || data.city || "",
            name: extraData.name || data.name || "",
            age: extraData.age || "",
            brandName: extraData.brandName || "",
            commercialVehicleAge: extraData.commercialVehicleAge || "",
            previousVehicleOwnership: extraData.previousVehicleOwnership || "",
            previousVehicleFuelType: extraData.previousVehicleFuelType || "",
            previousVehicleSegment: extraData.previousVehicleSegment || "",
            whoWillDrive: extraData.whoWillDrive || "",
            ownership: extraData.ownership || "",
            callStatus: extraData.callStatus || "",
            connectedOutcome: extraData.connectedOutcome || "",
            notConnectedOutcome: extraData.notConnectedOutcome || "",
            leadSource: extraData.leadSource || "",
            qualification: extraData.qualification || "",
            fromWhereGetNumber: extraData.fromWhereGetNumber || "",
            description: extraData.description || "",
         
          }));

          // If demo details exist, populate them
          if (extraData.demoDetails) {
            setDemoDetails({
              customerName: extraData.demoDetails.customerName || "",
              customerMobile: extraData.demoDetails.customerMobile || "",
              classification: extraData.demoDetails.classification || "",
              findCity: extraData.demoDetails.findCity || "",
              hotCold: extraData.demoDetails.hotCold || "",
              address1: extraData.demoDetails.address1 || "",
              address2: extraData.demoDetails.address2 || "",
              googleMaps: extraData.demoDetails.googleMaps || "",
            });
          }
        } else {
          // If API fails, fallback to leadData
          setForm(prev => ({ ...prev, ...leadData }));
        }
      } catch (error) {
        console.error("Failed to fetch existing data:", error);
        // Fallback to leadData on error
        setForm(prev => ({ ...prev, ...leadData }));
      } finally {
        setLoading(false);
        setDataFetched(true);
      }
    };

    fetchExistingData();
  }, [leadData]);

  const showConnectedOutcome = form.callStatus === "connected";
  const showDemoScheduled =
    showConnectedOutcome &&
    String(form.connectedOutcome).toLowerCase() === "demo scheduled";
  const showNotConnected = [
    "not connected",
    "not responding",
    "wrong number",
  ].includes(String(form.callStatus).toLowerCase());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDemoChange = (e) => {
    const { name, value } = e.target;
    setDemoDetails((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.phoneNumber || String(form.phoneNumber).trim() === "")
      return "Phone Number required";
    if (!form.callStatus || String(form.callStatus).trim() === "")
      return "Follow-up required";
    if (
      showConnectedOutcome &&
      (!form.connectedOutcome || String(form.connectedOutcome).trim() === "")
    )
      return "Connected outcome required";
    if (showDemoScheduled) {
      if (!demoDetails.customerName || !demoDetails.customerMobile)
        return "Demo details required: Customer Name & Mobile Number";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const payload = {
      ...form,
      ...(showDemoScheduled ? { demoDetails } : {}),
    };

    try {
      // Use edit API only if we successfully fetched existing data
      const dealId = leadData._id;
      const leadId = leadData.id;
      
      let res;
      if (hasExistingData) {
        // Use edit API - we have existing data
        const editPayload = {
          ...(dealId ? { dealId } : { id: leadId }),
          ...payload,
        };
        console.log('Edit payload:', editPayload); // Debug log
        res = await apiRequest("patch", "/stage-detail/stage-transition-form-edit", editPayload);
      } else {
        // Use create API - no existing data found
        res = await apiRequest("post", "/stage-detail/stage-transition-form", payload);
      }

      const updatedLead = res.lead || res.deal || res;

      // call parent handler with updated lead returned from API (or raw response)
      if (onSubmit) onSubmit(updatedLead);
      // simple feedback
      alert("Saved successfully");
    } catch (e) {
      console.error("StageTransitionForm submit error:", e);
      alert(e.message || "Submit failed");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "900px",
          height: "400px",
          bgcolor: "background.paper",
          borderRadius: "24px 0 0 24px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "900px",
        bgcolor: "background.paper",
        borderRadius: "24px 0 0 24px",
        boxShadow: 3,
        clipPath: "inset(0 0 0 0)",
        maxHeight: "85vh",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Header with close icon to match other dialogs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 3,
          pr: 0,
          py: 0.75,
          mx: 3,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Lead Details
        </Typography>
        <IconButton
          onClick={() => onCancel && onCancel()}
          sx={{ color: "var(--primary-color)" }}
          size="small"
          aria-label="close"
        >
          <FiX />
        </IconButton>
      </Box>
      <Box sx={{ pl: 3, pr: 0, py: 2, overflowY: "auto", flex: 1 }}>
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: "repeat(2, 1fr)",
        }}
      >
        <TextField
          fullWidth
          label="First Name"
          name="firstName"
          value={form.firstName}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="Last Name"
          name="lastName"
          value={form.lastName}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="Phone Number"
          name="phoneNumber"
          value={form.phoneNumber}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="Email Id"
          name="emailId"
          value={form.emailId}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="City"
          name="city"
          value={form.city}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="Age"
          name="age"
          value={form.age}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          label="Brand Name"
          name="brandName"
          value={form.brandName}
          onChange={handleChange}
          size="small"
        />
        <TextField
          fullWidth
          select
          label="Commercial Vehicle Age"
          name="commercialVehicleAge"
          value={form.commercialVehicleAge}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="0-1">0 - 1 Years</MenuItem>
          <MenuItem value="1-3">1 - 3 Years</MenuItem>
          <MenuItem value="3-5">3 - 5 Years</MenuItem>
          <MenuItem value="5-7">5 - 7 Years</MenuItem>
          <MenuItem value="7-10">7 - 10 Years</MenuItem>
          <MenuItem value=">10">More than 10 Years</MenuItem>
          <MenuItem value="na">N/A (No commercial vehicle)</MenuItem>
        </TextField>

        <TextField
          fullWidth
          select
          label="Previous Vehicle Ownership"
          name="previousVehicleOwnership"
          value={form.previousVehicleOwnership}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="first">First Owner</MenuItem>
          <MenuItem value="second">Second Owner</MenuItem>
          <MenuItem value="third_more">Third or More Owners</MenuItem>
          <MenuItem value="na">N/A (No previous vehicle)</MenuItem>
        </TextField>

        <TextField
          fullWidth
          select
          label="Previous Vehicle Fuel Type"
          name="previousVehicleFuelType"
          value={form.previousVehicleFuelType}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="diesel">Diesel</MenuItem>
          <MenuItem value="petrol">Petrol</MenuItem>
          <MenuItem value="cng">CNG</MenuItem>
          <MenuItem value="lpg">LPG</MenuItem>
          <MenuItem value="electric">Electric</MenuItem>
          <MenuItem value="hybrid">Hybrid</MenuItem>
          <MenuItem value="na">N/A</MenuItem>
        </TextField>

        <TextField
          fullWidth
          select
          label="Previous Vehicle Segment"
          name="previousVehicleSegment"
          value={form.previousVehicleSegment}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="lcv">LCV (Light Commercial Vehicle)</MenuItem>
          <MenuItem value="mcv">MCV (Medium Commercial Vehicle)</MenuItem>
          <MenuItem value="hcv">HCV (Heavy Commercial Vehicle)</MenuItem>
          <MenuItem value="scv">SCV / Pick-up</MenuItem>
          <MenuItem value="bus">Bus / Coach</MenuItem>
          <MenuItem value="tractor">Tractor / Trailer</MenuItem>
          <MenuItem value="others">Others / Not Applicable</MenuItem>
        </TextField>

        <TextField
          fullWidth
          select
          label="Who will drive?"
          name="whoWillDrive"
          value={form.whoWillDrive}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="self">Self (Owner will drive)</MenuItem>
          <MenuItem value="hired">
            Hired Driver (Driver will be appointed)
          </MenuItem>
          <MenuItem value="both">Both (Owner and Hired Driver)</MenuItem>
        </TextField>

        <TextField
          fullWidth
          select
          label="Ownership"
          name="ownership"
          value={form.ownership}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="individual">Individual / Proprietary</MenuItem>
          <MenuItem value="partnership">Partnership Firm</MenuItem>
          <MenuItem value="private_limited">Private Limited Company</MenuItem>
          <MenuItem value="public_limited">Public Limited Company</MenuItem>
          <MenuItem value="trust">Trust / Society / NGO</MenuItem>
          <MenuItem value="other">Other (please specify)</MenuItem>
        </TextField>
      </Box>

      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
        Basic Details
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <TextField
          fullWidth
          select
          label="Follow-up"
          name="callStatus"
          value={form.callStatus}
          onChange={handleChange}
          size="small"
        >
          <MenuItem value="connected">Connected Follow-up</MenuItem>
          <MenuItem value="not connected">NC Follow-up</MenuItem>
          <MenuItem value="not responding">NR Follow-up</MenuItem>
          <MenuItem value="final drop">Final Drop</MenuItem>
          
        </TextField>

        {showConnectedOutcome && (
          <TextField
            fullWidth
            select
            label="Connected Outcome"
            name="connectedOutcome"
            value={form.connectedOutcome}
            onChange={handleChange}
            size="small"
          >
            <MenuItem value="connected follow up call">
              Connected Follow Up Call
            </MenuItem>
            <MenuItem value="demo scheduled">Demo Scheduled</MenuItem>
            <MenuItem value="general enquiry">General Enquiry</MenuItem>
            <MenuItem value="plan postponed">Plan Postponed</MenuItem>
            <MenuItem value="out of geo limit">Out of Geo Limit</MenuItem>
            <MenuItem value="not interested">Not Interested</MenuItem>
            <MenuItem value="cibil">CIBIL</MenuItem>
          </TextField>
        )}

        {showNotConnected && (
          <>
            <TextField
              fullWidth
              select
              label="Not Connected Outcome"
              name="notConnectedOutcome"
              value={form.notConnectedOutcome}
              onChange={handleChange}
              size="small"
            >
              <MenuItem value="NC Follow up call">NC Follow up call</MenuItem>
              <MenuItem value="NC dropped">NC dropped</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Description / Remark"
              name="description"
              value={form.description}
              onChange={handleChange}
              multiline
              minRows={2}
              size="small"
            />
          </>
        )}

        {showDemoScheduled && (
          <Box sx={{ mt: 2, p: 2.5, border: "1px solid #e0e0e0", borderRadius: 2, bgcolor: "#fafafa" }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Demo Scheduled Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Customer Name"
                name="customerName"
                value={demoDetails.customerName}
                onChange={handleDemoChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Mobile Number"
                name="customerMobile"
                value={demoDetails.customerMobile}
                onChange={handleDemoChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Classification Of leads"
                name="classification"
                value={demoDetails.classification}
                onChange={handleDemoChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Find city"
                name="findCity"
                value={demoDetails.findCity}
                onChange={handleDemoChange}
                size="small"
              />
              <TextField
                fullWidth
                select
                label="Hot/Cold lead"
                name="hotCold"
                value={demoDetails.hotCold}
                onChange={handleDemoChange}
                size="small"
              >
                <MenuItem value="hot">Hot</MenuItem>
                <MenuItem value="cold">Cold</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Addressline 1"
                name="address1"
                value={demoDetails.address1}
                onChange={handleDemoChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Addressline 2"
                name="address2"
                value={demoDetails.address2}
                onChange={handleDemoChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Google maps link"
                name="googleMaps"
                value={demoDetails.googleMaps}
                onChange={handleDemoChange}
                size="small"
              />
            </Stack>
          </Box>
        )}

        {/* Common fields shown when not in NotConnected/demo-specific UI */}
        {!showNotConnected && !showDemoScheduled && (
          <>
            <TextField
              fullWidth
              select
              label="Lead Source"
              name="leadSource"
              value={form.leadSource}
              onChange={handleChange}
              size="small"
            >
              <MenuItem value="digital_marketing">Digital Marketing </MenuItem>
              <MenuItem value="referral">Referral</MenuItem>
              <MenuItem value="direct_walkin_dealer_visit">
                Direct Walk-in / Dealer Visit
              </MenuItem>
              <MenuItem value="telecalling_outbound">
                Telecalling / Outbound{" "}
              </MenuItem>
              <MenuItem value="events_trade_show">Events / Trade Show</MenuItem>
              <MenuItem value="other_unknown">Other / Unknown</MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              label="Qualification"
              name="qualification"
              value={form.qualification}
              onChange={handleChange}
              size="small"
            >
              <MenuItem value="qualified">Qualified</MenuItem>
              <MenuItem value="unqualified">Unqualified</MenuItem>
            </TextField>

            <TextField
              fullWidth
              select
              label="From where you get our Number"
              name="fromWhereGetNumber"
              value={form.fromWhereGetNumber}
              onChange={handleChange}
              size="small"
            >
              <MenuItem value="website">Website </MenuItem>
              <MenuItem value="google_maps">Google/Maps </MenuItem>
              <MenuItem value="social_media_page">Social Media Page </MenuItem>
              <MenuItem value="friend_reference">Friend / Reference </MenuItem>
              <MenuItem value="printed_ad">Printed Ad</MenuItem>
              <MenuItem value="directory_listing_site">
                Directory / Listing Site{" "}
              </MenuItem>
              <MenuItem value="received_sms_whatsapp">
                Received SMS / WhatsApp{" "}
              </MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Description / Remarks"
              name="description"
              value={form.description}
              onChange={handleChange}
              multiline
              minRows={2}
              size="small"
            />
          </>
        )}
      </Box>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ pl: 3, pr: 3, py: 1, mx: 3, borderTop: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: "background.paper" }}
      >
        <Button variant="outlined" onClick={onCancel} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ background: "var(--primary-color)" }}
          size="small"
        >
          Submit
        </Button>
      </Stack>
    </Box>
  );
}
